import { useCallback, useState } from 'react';

const KEY = 'fuse.adminMode';

export function adminModeStored(): boolean {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * `on`: admin mode, a view choice. `sandbox`: this browser is on the admin's test account (ADR-0014), which the server
 * keeps in a cookie and reports in /api/me; `setSandbox` switches it, and the app reloads memory for the new account.
 */
export type AdminMode = {
  on: boolean;
  set(on: boolean): void;
  sandbox: boolean;
  setSandbox(on: boolean): Promise<void>;
};
type ViewMode = Pick<AdminMode, 'on' | 'set'>;

/**
 * Admins choose, per browser, whether they see admin tools. Off (the default), the app is exactly what users get.
 * This is only a view choice: the server checks the account on every admin route.
 */
export function useAdminMode(isAdmin: boolean): ViewMode & { admin: ViewMode | null } {
  const [on, setOn] = useState(adminModeStored);
  const set = useCallback((next: boolean) => {
    try {
      if (next) localStorage.setItem(KEY, '1');
      else localStorage.removeItem(KEY);
    } catch {
      // Private mode or blocked storage: the choice lasts until reload.
    }
    setOn(next);
  }, []);
  // `admin` is null for everyone else; `set` stays usable so sign-out can switch the mode off either way.
  return { on, set, admin: isAdmin ? { on, set } : null };
}
